import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../../../services/api/client';

export type AiEvaluationJob = {
  submissionId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  error?: string | null;
  results: Array<{
    provider: 'qwen' | 'doubao' | 'glm';
    model?: string; evaluation?: string; error?: string; durationMs: number;
  }>;
};

export function useAiEvaluation(submissionId: string, enabled: boolean) {
  const [job, setJob] = useState<AiEvaluationJob | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const version = useRef(0);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; version.current++; }; }, []);

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    const read = async () => {
      const current = ++version.current;
      try {
        const response = await apiClient.get<AiEvaluationJob | null>(
          `/ai-evaluation/submissions/${encodeURIComponent(submissionId)}`, { signal: controller.signal },
        );
        if (disposed || current !== version.current) return;
        const value = response.data;
        if (value && value.submissionId !== submissionId) throw new Error('评价记录与当前作业不匹配');
        setJob(value); setError(''); setLoading(false);
        if (value?.status === 'queued' || value?.status === 'running') timer = setTimeout(read, 2000);
      } catch (error: any) {
        if (disposed || current !== version.current) return;
        setError(error.response?.data?.message ?? '读取 AI 评价失败，正在重试');
        setLoading(false);
        timer = setTimeout(read, 5000);
      }
    };
    void read();
    return () => { disposed = true; clearTimeout(timer); controller.abort(); };
  }, [submissionId, enabled, refresh]);

  const enqueue = useCallback(async (retry = false) => {
    // Discard any older GET that completes after this POST.
    const current = ++version.current;
    setLoading(true); setError('');
    try {
      const response = await apiClient.post<AiEvaluationJob>(
        `/ai-evaluation/submissions/${encodeURIComponent(submissionId)}`, { retry },
      );
      if (!mounted.current || current !== version.current) return;
      setJob(response.data);
    } finally {
      if (mounted.current) { setLoading(false); setRefresh(value => value + 1); }
    }
  }, [submissionId]);
  return { job, loading, error, enqueue };
}
